import { useCallback, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Facebook, Film, Instagram, Youtube } from "lucide-react";
import { FACEBOOK, fashionAlbums, fashionClips, IMDB, INSTAGRAM, runwayClips, YOUTUBE } from "@/data/content";
import { Eyebrow, Reveal, SectionTitle } from "./Reveal";
import { ReelModal, ReelStrip } from "./Showreel";
import { Lightbox } from "./Lightbox";

const AlbumCard = ({ album, index, onOpen }) => (
  <button data-testid={`fashion-album-${album.id}`} onClick={onOpen} aria-label={`Open ${album.title} album`} className="group block w-full text-left">
    <div className="relative aspect-[4/5] mr-2 mb-2 sm:mr-4 sm:mb-4">
      <span className="absolute inset-0 translate-x-2 translate-y-2 sm:translate-x-4 sm:translate-y-4 bg-[var(--nude)] border border-black/5 group-hover:translate-x-3 group-hover:translate-y-3 sm:group-hover:translate-x-5 sm:group-hover:translate-y-5 transition-transform duration-700" />
      <span className="absolute inset-0 translate-x-1 translate-y-1 sm:translate-x-2 sm:translate-y-2 bg-white border border-black/5 group-hover:translate-x-1.5 group-hover:translate-y-1.5 sm:group-hover:translate-x-3 sm:group-hover:translate-y-3 transition-transform duration-700" />
      <span className="absolute inset-0 overflow-hidden shadow-[0_30px_60px_-30px_rgba(60,40,20,0.45)]">
        <img src={album.cover} alt={`${album.title} album cover`} loading="lazy" style={{ objectPosition: album.coverPos || "50% 15%" }}
          className="h-full w-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out" />
        <span className="absolute bottom-0 inset-x-0 p-3 sm:p-5 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-between gap-2 text-white">
          <span className="hidden sm:inline text-[12px] uppercase tracking-[0.22em]">View album</span>
          <span data-testid={`fashion-album-${album.id}-count`} className="text-[12px] tracking-[0.2em]">{String(album.items.length).padStart(2, "0")} PHOTOS</span>
        </span>
      </span>
    </div>
    <p className="mt-5 flex items-baseline gap-3">
      <span className="text-[12px] tracking-[0.2em] text-[var(--gold)]">{String(index + 1).padStart(2, "0")}</span>
      <span className="font-display text-2xl sm:text-3xl text-stone-900 group-hover:text-[var(--gold)] transition-colors duration-500">{album.title}</span>
    </p>
    <p className="mt-1 text-[12px] uppercase tracking-[0.18em] text-stone-600">{album.sub}</p>
  </button>
);

const Albums = ({ onOpen }) => (
  <div data-testid="fashion-albums" className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-12 sm:gap-x-8">
    {fashionAlbums.map((a, i) => (
      <Reveal key={a.id} delay={i * 0.08}>
        <AlbumCard album={a} index={i} onOpen={() => onOpen(a.id)} />
      </Reveal>
    ))}
  </div>
);

const SOCIALS = [
  { id: "instagram", href: INSTAGRAM, Icon: Instagram, label: "Instagram" },
  { id: "facebook", href: FACEBOOK, Icon: Facebook, label: "Facebook" },
  { id: "youtube", href: YOUTUBE, Icon: Youtube, label: "YouTube" },
  { id: "imdb", href: IMDB, Icon: Film, label: "IMDb" },
];

const SocialIcons = () => (
  <div data-testid="modeling-socials" className="mt-8 flex items-center gap-3">
    {SOCIALS.map(({ id, href, Icon, label }) => (
      <a key={id} data-testid={`modeling-${id}-link`} href={href} target="_blank" rel="noreferrer" aria-label={label} title={label}
        className="grid h-11 w-11 place-items-center rounded-full border border-[var(--gold)]/40 text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors duration-500">
        <Icon size={17} strokeWidth={1.5} />
      </a>
    ))}
  </div>
);

export const Modeling = () => {
  const [open, setOpen] = useState(null);
  const [album, setAlbum] = useState(null);
  const close = useCallback(() => setOpen(null), []);
  const closeAlbum = useCallback(() => setAlbum(null), []);
  const activeAlbum = fashionAlbums.find((a) => a.id === album);
  return (
  <section id="modeling" data-testid="modeling-section" className="pt-36 sm:pt-44 pb-28">
    <div className="max-w-[1500px] mx-auto px-5 sm:px-10">
      <Reveal className="mb-14">
        <Eyebrow className="mb-6">02 — Fashion</Eyebrow>
        <SectionTitle>The world of <em className="text-[var(--gold)]">FASHION.</em></SectionTitle>
        <SocialIcons />
      </Reveal>
      <Albums onOpen={setAlbum} />
      <ReelStrip items={runwayClips} title="Project Runway — On camera" testid="runway-video" cols="sm:grid-cols-3" onOpen={(i) => setOpen({ list: "runway", i })} />
      <ReelStrip items={fashionClips} title="Campaigns — On camera" testid="fashion-video" cols="sm:grid-cols-3" onOpen={(i) => setOpen({ list: "fashion", i })} />
    </div>
    <AnimatePresence>{open !== null && (
      <ReelModal items={open.list === "fashion" ? fashionClips : runwayClips} title={open.list === "fashion" ? "Campaigns" : "Project Runway"} start={open.i} onClose={close} />
    )}</AnimatePresence>
    <AnimatePresence>{activeAlbum && <Lightbox items={activeAlbum.items} start={0} onClose={closeAlbum} />}</AnimatePresence>
  </section>
  );
};
